import { Select } from 'antd';
import React from 'react';

interface ArrayTextInputProps {
  value?: string[] | string;
  onChange: (value: string[]) => void;
  placeholder?: string;
}

/**
 * Array text input component for filter system
 * Allows users to enter multiple text values (comma-separated or as tags)
 * Used for array fields like medications, allergies, skills, languages
 *
 * Operator meanings:
 * - "Equals exactly" (==): Exact array match
 * - "Has any of" (overlap): Record has at least one of your values
 * - "Has all of" (contained_in): Record has all of your values
 */
export const ArrayTextInput: React.FC<ArrayTextInputProps> = ({ value, onChange, placeholder }) => {
  // Normalize value to always be an array
  const normalizedValue = React.useMemo(() => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string')
      return value
        .split(',')
        .map((v: string) => v.trim())
        .filter(Boolean);
    return [];
  }, [value]);

  const handleChange = (newValue: string[]) => {
    onChange(newValue);
  };

  // Prevent Enter key from propagating and closing the modal/dropdown
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
    }
  };

  return (
    <Select
      mode="tags"
      className="w-full"
      placeholder={placeholder || 'Type and press Enter to add...'}
      value={normalizedValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      allowClear
      style={{ width: '100%' }}
      tokenSeparators={[',']}
      maxTagCount="responsive"
    />
  );
};
